import { useCallback } from 'react';
import uuid from 'react-uuid';
import { MODELS_USER_GROUPS_KEY, MODELS_VIEWS_KEY } from 'dashboard409a/pages/Workboard/common/constants';
import { ModelView, ViewUserId } from 'dashboard409a/pages/Workboard/ModelViewsSelector/ModelViewsSelector.props';
import { UserGroup } from 'dashboard409a/pages/Workboard/UserGroups/UserGroups.types';
import { useLocalStorage } from 'services/hooks/useLocalStorage';

export const useUserGroups = () => {
  const [groups, setGroups] = useLocalStorage<UserGroup[]>(MODELS_USER_GROUPS_KEY, []);

  const createGroup = useCallback(
    (name: string, userIds: number[], isDefault: boolean) => {
      const newGroup: UserGroup = { id: uuid(), name, userIds, isDefault };
      setGroups(prev => {
        const updated = isDefault
          ? prev.map(g => ({ ...g, isDefault: false }))
          : prev;
        return [...updated, newGroup];
      });
      return newGroup;
    },
    [setGroups]
  );

  const updateGroup = useCallback(
    (updated: UserGroup): Promise<ModelView[] | null> => {
      setGroups(prev => {
        const withCleared = updated.isDefault
          ? prev.map(g => ({ ...g, isDefault: false }))
          : prev;
        return withCleared.map(g => (g.id === updated.id ? updated : g));
      });

      // Return the promise so callers that read MODELS_VIEWS_KEY immediately after
      // (e.g. onGroupUpdated handlers) can await the synced views instead of
      // reading a stale/missing localStorage value post-migration.
      return (async () => {
        try {
          const { getPreference, migrateLegacyLocalStorageKey, setPreference } = await import(
            'services/storage/preferencesIndexedDB'
          );
          await migrateLegacyLocalStorageKey(MODELS_VIEWS_KEY);
          const views = await getPreference<ModelView[]>(MODELS_VIEWS_KEY);
          if (!views?.length) return null;

          const synced = views.map(v => {
            const syncIds = (ids: ViewUserId[]): { ids: ViewUserId[]; changed: boolean } => {
              const hasGroupEntry = ids.some(p => p.groupId === updated.id);
              if (!hasGroupEntry) {
                return { ids, changed: false };
              }

              const kept = ids.filter(p => p.groupId !== updated.id || updated.userIds.includes(p.userId));
              const existingUserIds = new Set(kept.map(p => p.userId));
              const toAdd: ViewUserId[] = updated.userIds
                .filter(uid => !existingUserIds.has(uid))
                .map(uid => ({ userId: uid, groupId: updated.id }));
              const result = [...kept, ...toAdd];
              const changed =
                result.length !== ids.length ||
                result.some((p, i) => p.userId !== ids[i]?.userId || p.groupId !== ids[i]?.groupId);
              return { ids: result, changed };
            };

            const prepResult = syncIds(v.preparerIds ?? []);
            const revResult = syncIds(v.reviewerIds ?? []);
            if (!prepResult.changed && !revResult.changed) {
              return v;
            }
            return {
              ...v,
              preparerIds: prepResult.ids,
              reviewerIds: revResult.ids,
            };
          });
          await setPreference(MODELS_VIEWS_KEY, synced); // broadcastPreferenceChange inside setPreference already fires localStorageChange
          return synced;
        } catch {
          // persistence unavailable — skip silently
          return null;
        }
      })();
    },
    [setGroups]
  );

  const deleteGroup = useCallback(
    (groupId: string) => {
      setGroups(prev => prev.filter(g => g.id !== groupId));
    },
    [setGroups]
  );

  const defaultGroup = groups.find(g => g.isDefault) ?? null;

  return { groups, defaultGroup, createGroup, updateGroup, deleteGroup };
};
